// Step 3 do wizard: revisão visual + submit.
import { useMemo } from 'react';
import type { CreatePlanInput } from '@control-center/shared';

import { PillButton } from '../../../components/clients/PillButton';
import type { ExternalPlanEntity } from '../../../lib/external-plans-api';
import type { SaasProductEntity } from '../../../lib/saas-products-api';

interface Step3ReviewProps {
  data: CreatePlanInput;
  saasProducts: SaasProductEntity[];
  externalPlans: ExternalPlanEntity[];
  isSubmitting: boolean;
  onBack: () => void;
  onSubmit: () => Promise<void>;
  onCancel: () => void;
}

export function Step3Review({
  data,
  saasProducts,
  externalPlans,
  isSubmitting,
  onBack,
  onSubmit,
  onCancel,
}: Step3ReviewProps): JSX.Element {
  const saasMap = useMemo(() => new Map(saasProducts.map((s) => [s.id, s])), [saasProducts]);
  const extMap = useMemo(() => new Map(externalPlans.map((p) => [p.id, p])), [externalPlans]);

  return (
    <div className="space-y-5">
      <section>
        <h3 className="m-0 text-[14px] font-semibold uppercase tracking-wider text-muted-3">
          Dados
        </h3>
        <dl className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <Item label="Nome" value={data.name} />
          <Item label="Slug" value={data.slug} mono />
          <Item label="Tipo" value={data.type ?? '—'} />
          <Item label="Status" value={data.status ?? '—'} />
          <Item label="Ciclo" value={data.billing_cycle ?? '—'} />
          <Item label="Trial (dias)" value={String(data.trial_days ?? 0)} />
        </dl>
        {data.description && (
          <p className="mt-3 rounded-[8px] bg-panel-2 p-3 text-[13px] text-muted">
            {data.description}
          </p>
        )}
      </section>

      <section className="border-t border-line pt-5">
        <h3 className="m-0 text-[14px] font-semibold uppercase tracking-wider text-muted-3">
          Precificação
        </h3>
        <dl className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <Item label="Preço mensal" value={formatBRL(data.monthly_price ?? 0)} />
          <Item
            label="Preço anual"
            value={data.annual_price != null ? formatBRL(data.annual_price) : '—'}
          />
          <Item label="Setup fee" value={formatBRL(data.setup_fee ?? 0)} />
          <Item
            label="Preço promocional"
            value={data.promo_price != null ? formatBRL(data.promo_price) : '—'}
          />
        </dl>
      </section>

      <section className="border-t border-line pt-5">
        <h3 className="m-0 text-[14px] font-semibold uppercase tracking-wider text-muted-3">
          Entitlements ({data.entitlements?.length ?? 0})
        </h3>
        {data.entitlements && data.entitlements.length > 0 ? (
          <ul className="mt-3 flex flex-col gap-2">
            {data.entitlements.map((e, idx) => {
              const saas = saasMap.get(e.saas_product_id);
              const ext = extMap.get(e.external_plan_id);
              const overrideKeys = Object.keys(
                (e.limits_override as Record<string, unknown> | undefined) ?? {},
              );
              return (
                <li key={idx} className="rounded-[8px] border border-line bg-panel-2 p-3">
                  <div className="text-[13px] font-medium text-ink">
                    {saas?.name ?? '(saas inválido)'} → {ext?.name ?? '(plano inválido)'}
                  </div>
                  <code className="text-[11px] text-muted-2">
                    {saas?.slug ?? '?'}/{ext?.slug ?? '?'}
                  </code>
                  {overrideKeys.length > 0 && (
                    <pre className="mt-2 overflow-x-auto rounded-[6px] bg-panel-3/50 p-2 font-mono text-[11px] text-muted-2">
                      {JSON.stringify(e.limits_override, null, 2)}
                    </pre>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mt-3 text-[13px] text-muted">Sem entitlements (service_only).</p>
        )}
      </section>

      <div className="flex items-center justify-between gap-2 border-t border-line pt-4">
        <PillButton type="button" onClick={onBack} disabled={isSubmitting}>
          ← Voltar
        </PillButton>
        <div className="flex items-center gap-2">
          <PillButton type="button" onClick={onCancel} disabled={isSubmitting}>
            Cancelar
          </PillButton>
          <PillButton solid type="button" onClick={() => void onSubmit()} disabled={isSubmitting}>
            {isSubmitting ? 'Criando…' : 'Criar plano'}
          </PillButton>
        </div>
      </div>
    </div>
  );
}

function Item({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-muted-3">{label}</dt>
      <dd className={`mt-1 text-[13px] text-ink ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  );
}

function formatBRL(value: number): string {
  if (Number.isNaN(value)) return 'R$ —';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
